/**
 * AdminAnalytics Component
 * Charts for user growth and revenue with toggleable chart types and date filters
 */

import React, { useState, useEffect } from 'react';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import type { UserAnalytics, RevenueAnalytics } from '../../services/adminService';
import { analyticsApi } from '../../services/adminService';
import useAdminAuth from '../../hooks/useAdminAuth';
import AdminLayout from './AdminLayout';
import { API_BASE_URL } from '../../config/api';

type ChartType = 'line' | 'area' | 'bar';
type Period = '7d' | '30d' | '1y' | 'custom';

interface ComprehensiveMetrics {
  userGrowth: {
    totalSignups: number;
    newUsers7d: number;
    newUsers30d: number;
    onboardingCompletionPercent: number;
    dropoffStages: {
      noTasks: number;
      afterAbout: number;
      afterLegacy: number;
      afterInventory: number;
      afterFuneral: number;
      afterPersonal: number;
    };
  };
  personal: {
    personalPercent: number;
    funeralPercent: number;
    legacyPercent: number;
    petsPercent: number;
    overallPercent: number;
    itemsLogged: number;
    incompleteWorkflows: number;
  };
  financial: {
    inventoryPercent: number;
    debtsPercent: number;
    distributionPercent: number;
    overallPercent: number;
    accountsAndAssetsLogged: number;
    debtsLogged: number;
  };
  legal: {
    legalEssentialsPercent: number;
    executorsPercent: number;
    overallPercent: number;
    documentsUploaded: number;
  };
  executors: {
    executorsAdded: number;
    verificationPercent: number;
    loginRatePercent: number;
  };
  engagement: {
    dailyActiveUsers: number;
    weeklyActiveUsers: number;
    avgSessionsPerUser: string;
  };
  security: {
    failedLogins7d: number;
    passwordResets7d: number;
  };
}

const AdminAnalytics: React.FC = () => {
  const { isAdmin, isLoading: authLoading } = useAdminAuth();
  const [userPeriod, setUserPeriod] = useState<Period>('7d');
  const [revenuePeriod, setRevenuePeriod] = useState<Period>('7d');
  const [userChartType, setUserChartType] = useState<ChartType>('line');
  const [revenueChartType, setRevenueChartType] = useState<ChartType>('area');
  
  const [userStartDate, setUserStartDate] = useState<string>('');
  const [userEndDate, setUserEndDate] = useState<string>('');
  const [revenueStartDate, setRevenueStartDate] = useState<string>('');
  const [revenueEndDate, setRevenueEndDate] = useState<string>('');

  const [compStartDate, setCompStartDate] = useState<string>('');
  const [compEndDate, setCompEndDate] = useState<string>('');

  const [userAnalytics, setUserAnalytics] = useState<UserAnalytics | null>(null);
  const [revenueAnalytics, setRevenueAnalytics] = useState<RevenueAnalytics | null>(null);
  const [comprehensiveMetrics, setComprehensiveMetrics] = useState<ComprehensiveMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!isAdmin) return;
      
      try {
        const token = localStorage.getItem('account-token');
        
        const userParams = userPeriod === 'custom' && userStartDate && userEndDate
          ? { startDate: userStartDate, endDate: userEndDate }
          : { period: userPeriod };

        const revenueParams = revenuePeriod === 'custom' && revenueStartDate && revenueEndDate
          ? { startDate: revenueStartDate, endDate: revenueEndDate }
          : { period: revenuePeriod };

        const compUrl = new URL(`${API_BASE_URL}/api/admin/analytics/comprehensive`);
        if (compStartDate && compEndDate) {
          compUrl.searchParams.set('startDate', compStartDate);
          compUrl.searchParams.set('endDate', compEndDate);
        }

        const [userData, revenueData, comprehensiveResponse] = await Promise.all([
          analyticsApi.getUserAnalytics(userParams),
          analyticsApi.getRevenueAnalytics(revenueParams),
          fetch(compUrl.toString(), {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })
        ]);
        
        setUserAnalytics(userData);
        setRevenueAnalytics(revenueData);
        
        console.log('[AdminAnalytics] Comprehensive response status:', comprehensiveResponse.status);
        
        if (comprehensiveResponse.ok) {
          const comprehensiveData = await comprehensiveResponse.json();
          console.log('[AdminAnalytics] Comprehensive data received:', comprehensiveData);
          setComprehensiveMetrics(comprehensiveData.data);
        } else {
          const errorText = await comprehensiveResponse.text();
          console.error('[AdminAnalytics] Comprehensive metrics failed:', comprehensiveResponse.status, errorText);
          // Don't set global error - let old charts still display
        }
        
        setError(null);
      } catch (err) {
        console.error('[AdminAnalytics] Failed to fetch analytics:', err);
        setError('Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading && isAdmin) {
      fetchAnalytics();
    }
  }, [isAdmin, authLoading, userPeriod, revenuePeriod, userStartDate, userEndDate, revenueStartDate, revenueEndDate, compStartDate, compEndDate]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value / 100);
  };

  const periodLabels: Record<Period, string> = {
    '7d': '7 Days',
    '30d': '30 Days',
    '1y': '1 Year',
    'custom': 'Custom Range'
  };

  const chartTypeIcons: Record<ChartType, React.ReactNode> = {
    line: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
      </svg>
    ),
    area: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    bar: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
      </svg>
    )
  };

  const renderChart = (
    data: Array<{ date: string; [key: string]: string | number }>,
    dataKey: string,
    color: string,
    chartType: ChartType,
    formatValue?: (value: number) => string
  ) => {
    const commonProps = {
      data,
      margin: { top: 10, right: 30, left: 0, bottom: 0 }
    };

    const chartContent = (
      <>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis 
          dataKey="date" 
          stroke="#6b7280"
          fontSize={12}
          tickFormatter={(value) => {
            const date = new Date(value);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          }}
        />
        <YAxis 
          stroke="#6b7280" 
          fontSize={12}
          tickFormatter={formatValue}
        />
        <Tooltip 
          formatter={(value: number) => formatValue ? formatValue(value) : value}
          labelFormatter={(label) => new Date(label).toLocaleDateString()}
          contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
        />
        <Legend />
      </>
    );

    switch (chartType) {
      case 'area':
        return (
          <AreaChart {...commonProps}>
            {chartContent}
            <Area 
              type="monotone" 
              dataKey={dataKey} 
              stroke={color} 
              fill={color} 
              fillOpacity={0.3}
              name={dataKey === 'count' ? 'New Users' : 'Revenue'}
            />
          </AreaChart>
        );
      case 'bar':
        return (
          <BarChart {...commonProps}>
            {chartContent}
            <Bar 
              dataKey={dataKey} 
              fill={color}
              name={dataKey === 'count' ? 'New Users' : 'Revenue'}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        );
      case 'line':
      default:
        return (
          <LineChart {...commonProps}>
            {chartContent}
            <Line 
              type="monotone" 
              dataKey={dataKey} 
              stroke={color} 
              strokeWidth={2}
              dot={{ fill: color, strokeWidth: 2 }}
              name={dataKey === 'count' ? 'New Users' : 'Revenue'}
            />
          </LineChart>
        );
    }
  };

  if (authLoading || loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      </AdminLayout>
    );
  }

  const MetricCard = ({ title, value, subtitle, color = 'teal' }: {
    title: string;
    value: string | number;
    subtitle?: string;
    color?: string;
  }) => {
    const colorClasses: Record<string, string> = {
      'blue': 'text-blue-600',
      'cyan': 'text-cyan-600',
      'teal': 'text-teal-600',
      'purple': 'text-purple-600',
      'pink': 'text-pink-600',
      'green': 'text-green-600',
      'emerald': 'text-emerald-600',
      'amber': 'text-amber-600',
      'yellow': 'text-yellow-600',
      'indigo': 'text-indigo-600',
      'rose': 'text-rose-600',
      'red': 'text-red-600',
      'orange': 'text-orange-600'
    };

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <p className="text-sm text-gray-500">{title}</p>
        <p className={`text-2xl font-bold ${colorClasses[color] || 'text-teal-600'} mt-1`}>{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Comprehensive Analytics</h1>
          <p className="text-gray-600 mt-1">Platform metrics across all categories</p>
        </div>

        {/* Comprehensive Metrics Status */}
        {!comprehensiveMetrics && !loading && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800 font-medium">📊 Loading comprehensive metrics...</p>
            <p className="text-yellow-600 text-sm mt-1">Check browser console (F12) for details if this persists.</p>
          </div>
        )}

        {comprehensiveMetrics && (
          <>
            {/* Date Range Filter for Comprehensive Metrics */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-4">
              <span className="text-sm font-medium text-gray-700">Filter metrics by date range:</span>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={compStartDate}
                  onChange={(e) => setCompStartDate(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="date"
                  value={compEndDate}
                  onChange={(e) => setCompEndDate(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                />
              </div>
              {(compStartDate || compEndDate) && (
                <button
                  onClick={() => { setCompStartDate(''); setCompEndDate(''); }}
                  className="text-sm text-cyan-600 hover:text-cyan-800 font-medium"
                >
                  Clear
                </button>
              )}
              {compStartDate && compEndDate && (
                <span className="text-xs text-gray-500">
                  Showing data from {compStartDate} to {compEndDate}
                </span>
              )}
            </div>

            {/* 1. USER GROWTH AND ACTIVATION */}
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">📈 User Growth & Activation</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <MetricCard title="Total Signups" value={comprehensiveMetrics.userGrowth.totalSignups.toLocaleString()} color="blue" />
                <MetricCard title="New Users (7d)" value={comprehensiveMetrics.userGrowth.newUsers7d.toLocaleString()} color="blue" />
                <MetricCard title="New Users (30d)" value={comprehensiveMetrics.userGrowth.newUsers30d.toLocaleString()} color="cyan" />
                <MetricCard title="Onboarding Completion" value={`${comprehensiveMetrics.userGrowth.onboardingCompletionPercent}%`} subtitle="Setup Your Plan" color="teal" />
              </div>
              <div className="bg-white rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Drop-off Analysis</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
                  <div><span className="text-gray-500">No Tasks:</span> <span className="font-semibold">{comprehensiveMetrics.userGrowth.dropoffStages.noTasks}</span></div>
                  <div><span className="text-gray-500">After About:</span> <span className="font-semibold">{comprehensiveMetrics.userGrowth.dropoffStages.afterAbout}</span></div>
                  <div><span className="text-gray-500">After Legacy:</span> <span className="font-semibold">{comprehensiveMetrics.userGrowth.dropoffStages.afterLegacy}</span></div>
                  <div><span className="text-gray-500">After Inventory:</span> <span className="font-semibold">{comprehensiveMetrics.userGrowth.dropoffStages.afterInventory}</span></div>
                  <div><span className="text-gray-500">After Funeral:</span> <span className="font-semibold">{comprehensiveMetrics.userGrowth.dropoffStages.afterFuneral}</span></div>
                  <div><span className="text-gray-500">After Personal:</span> <span className="font-semibold">{comprehensiveMetrics.userGrowth.dropoffStages.afterPersonal}</span></div>
                </div>
              </div>
            </div>

            {/* 2. PERSONAL WORKFLOWS */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">💜 Personal Workflows</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <MetricCard title="Overall Completion" value={`${comprehensiveMetrics.personal.overallPercent}%`} color="purple" />
                <MetricCard title="Personal Items" value={`${comprehensiveMetrics.personal.personalPercent}%`} subtitle="Accounts & Belongings" color="purple" />
                <MetricCard title="Funeral Planning" value={`${comprehensiveMetrics.personal.funeralPercent}%`} color="purple" />
                <MetricCard title="Legacy & Memories" value={`${comprehensiveMetrics.personal.legacyPercent}%`} color="purple" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <MetricCard title="Pets Planned" value={`${comprehensiveMetrics.personal.petsPercent}%`} color="pink" />
                <MetricCard title="Items Logged" value={comprehensiveMetrics.personal.itemsLogged.toLocaleString()} color="pink" />
                <MetricCard title="Incomplete Workflows" value={comprehensiveMetrics.personal.incompleteWorkflows} color="pink" />
              </div>
            </div>

            {/* 3. FINANCIAL WORKFLOWS */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">💰 Financial Workflows</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <MetricCard title="Overall Completion" value={`${comprehensiveMetrics.financial.overallPercent}%`} color="green" />
                <MetricCard title="Inventory" value={`${comprehensiveMetrics.financial.inventoryPercent}%`} subtitle="Assets Complete" color="green" />
                <MetricCard title="Debts Tracking" value={`${comprehensiveMetrics.financial.debtsPercent}%`} color="green" />
                <MetricCard title="Distribution" value={`${comprehensiveMetrics.financial.distributionPercent}%`} color="green" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <MetricCard title="Accounts & Assets Logged" value={comprehensiveMetrics.financial.accountsAndAssetsLogged.toLocaleString()} color="emerald" />
                <MetricCard title="Debts/Expenses Logged" value={comprehensiveMetrics.financial.debtsLogged.toLocaleString()} color="emerald" />
              </div>
            </div>

            {/* 4. LEGAL WORKFLOWS */}
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">⚖️ Legal Workflows</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard title="Overall Completion" value={`${comprehensiveMetrics.legal.overallPercent}%`} color="amber" />
                <MetricCard title="Legal Essentials" value={`${comprehensiveMetrics.legal.legalEssentialsPercent}%`} color="amber" />
                <MetricCard title="Executors Assigned" value={`${comprehensiveMetrics.legal.executorsPercent}%`} color="amber" />
                <MetricCard title="Documents Uploaded" value={comprehensiveMetrics.legal.documentsUploaded.toLocaleString()} color="yellow" />
              </div>
            </div>

            {/* 5. EXECUTORS */}
            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">👥 Executors</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <MetricCard title="Executors Added" value={comprehensiveMetrics.executors.executorsAdded.toLocaleString()} color="indigo" />
                <MetricCard title="Verification Rate" value={`${comprehensiveMetrics.executors.verificationPercent}%`} subtitle="Accepted invites" color="indigo" />
                <MetricCard title="Login Rate" value={`${comprehensiveMetrics.executors.loginRatePercent}%`} subtitle="Active executors" color="blue" />
              </div>
            </div>

            {/* 6. ENGAGEMENT */}
            <div className="bg-gradient-to-r from-rose-50 to-red-50 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">📊 Engagement</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <MetricCard title="Daily Active Users" value={comprehensiveMetrics.engagement.dailyActiveUsers.toLocaleString()} color="rose" />
                <MetricCard title="Weekly Active Users" value={comprehensiveMetrics.engagement.weeklyActiveUsers.toLocaleString()} color="rose" />
                <MetricCard title="Avg Sessions/User" value={comprehensiveMetrics.engagement.avgSessionsPerUser} subtitle="Last 30 days" color="red" />
              </div>
            </div>

            {/* 7. SECURITY */}
            <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">🔒 Security</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <MetricCard title="Failed Logins (7d)" value={comprehensiveMetrics.security.failedLogins7d.toLocaleString()} color="red" />
                <MetricCard title="Password Resets (7d)" value={comprehensiveMetrics.security.passwordResets7d.toLocaleString()} color="orange" />
              </div>
            </div>
          </>
        )}

        {/* CHARTS SECTION */}
        <div className="pt-6 border-t border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-6">📈 Growth Charts</h2>
        </div>

        {/* User Growth Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">User Growth</h2>
              <p className="text-sm text-gray-500">
                Total: {userAnalytics?.total.toLocaleString() || 0} new users
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Chart Type Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                {(['line', 'area', 'bar'] as ChartType[]).map((type) => (
                  <button
                    key={type}
                    onClick={() => setUserChartType(type)}
                    className={`p-2 rounded-md transition-colors ${
                      userChartType === type 
                        ? 'bg-white shadow-sm text-teal-600' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    title={type.charAt(0).toUpperCase() + type.slice(1)}
                  >
                    {chartTypeIcons[type]}
                  </button>
                ))}
              </div>
              {/* Period Filter */}
              <select
                value={userPeriod}
                onChange={(e) => setUserPeriod(e.target.value as Period)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                {(['7d', '30d', '1y', 'custom'] as Period[]).map((p) => (
                  <option key={p} value={p}>{periodLabels[p]}</option>
                ))}
              </select>
            </div>
            {userPeriod === 'custom' && (
              <div className="flex flex-col sm:flex-row gap-3 items-center">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">From:</label>
                  <input
                    type="date"
                    value={userStartDate}
                    onChange={(e) => setUserStartDate(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">To:</label>
                  <input
                    type="date"
                    value={userEndDate}
                    onChange={(e) => setUserEndDate(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              </div>
            )}
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              {renderChart(
                userAnalytics?.dataPoints || [],
                'count',
                '#0d9488',
                userChartType
              )}
            </ResponsiveContainer>
          </div>
        </div>

        {/* Revenue Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Revenue</h2>
              <p className="text-sm text-gray-500">
                Total: {formatCurrency(revenueAnalytics?.total || 0)}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Chart Type Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                {(['line', 'area', 'bar'] as ChartType[]).map((type) => (
                  <button
                    key={type}
                    onClick={() => setRevenueChartType(type)}
                    className={`p-2 rounded-md transition-colors ${
                      revenueChartType === type 
                        ? 'bg-white shadow-sm text-emerald-600' 
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    title={type.charAt(0).toUpperCase() + type.slice(1)}
                  >
                    {chartTypeIcons[type]}
                  </button>
                ))}
              </div>
              {/* Period Filter */}
              <select
                value={revenuePeriod}
                onChange={(e) => setRevenuePeriod(e.target.value as Period)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                {(['7d', '30d', '1y', 'custom'] as Period[]).map((p) => (
                  <option key={p} value={p}>{periodLabels[p]}</option>
                ))}
              </select>
            </div>
            {revenuePeriod === 'custom' && (
              <div className="flex flex-col sm:flex-row gap-3 items-center">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">From:</label>
                  <input
                    type="date"
                    value={revenueStartDate}
                    onChange={(e) => setRevenueStartDate(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">To:</label>
                  <input
                    type="date"
                    value={revenueEndDate}
                    onChange={(e) => setRevenueEndDate(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              </div>
            )}
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              {renderChart(
                revenueAnalytics?.dataPoints || [],
                'amount',
                '#10b981',
                revenueChartType,
                formatCurrency
              )}
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAnalytics;

